import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowLeft, FaClock, FaEye, FaCheck, FaPaperPlane, FaFire, FaHandsClapping, FaHeart } from 'react-icons/fa';
import { arenaAPI } from '../utils/api';
import { toast } from 'sonner';

const EMOJI_MAP = {
  fire: { icon: '🔥', label: 'Fire' },
  clap: { icon: '👏', label: 'Clap' },
  mind_blown: { icon: '🤯', label: 'Mind Blown' },
  heart: { icon: '❤️', label: 'Heart' },
  '100': { icon: '💯', label: '100' },
  laugh: { icon: '😂', label: 'Laugh' },
  think: { icon: '🤔', label: 'Think' },
};

const formatTimeLeft = (expiresAt) => {
  if (!expiresAt) return '';
  const diff = new Date(expiresAt) - Date.now();
  if (diff <= 0) return 'Voting ended';
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return `${h}h ${m}m ${s}s`;
};

const FloatingReaction = ({ emoji, id }) => (
  <motion.div
    key={id}
    initial={{ opacity: 1, y: 0, x: Math.random() * 60 - 30, scale: 0.5 }}
    animate={{ opacity: 0, y: -120, scale: 1.2 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 2, ease: 'easeOut' }}
    className="absolute bottom-0 text-2xl pointer-events-none"
  >
    {EMOJI_MAP[emoji]?.icon || '🔥'}
  </motion.div>
);

const SpectatorView = () => {
  const { debateId } = useParams();
  const navigate = useNavigate();
  const [debate, setDebate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [voted, setVoted] = useState(null);
  const [spectatorCount, setSpectatorCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [floatingReactions, setFloatingReactions] = useState([]);
  const [timeLeft, setTimeLeft] = useState('');
  const commentsEndRef = useRef(null);
  const reactionIdRef = useRef(0);

  const loadDebate = useCallback(async () => {
    try {
      const res = await arenaAPI.getDebate(debateId);
      setDebate(res.data);
      setVoted(res.data.user_voted);
    } catch (err) {
      console.error('Failed to load debate', err);
      toast.error('Debate not found');
      navigate('/arena');
    } finally {
      setLoading(false);
    }
  }, [debateId, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadComments = useCallback(async () => {
    try {
      const res = await arenaAPI.getComments(debateId);
      setComments(res.data);
    } catch (err) { console.error('Failed to load comments', err); }
  }, [debateId]); // eslint-disable-line react-hooks/exhaustive-deps

  const pingSpectate = useCallback(async () => {
    try {
      const res = await arenaAPI.spectate(debateId);
      setSpectatorCount(res.data.spectator_count);
    } catch (err) { console.error('Failed to ping spectate', err); }
  }, [debateId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadDebate();
    loadComments();
    pingSpectate();

    const pollDebate = setInterval(loadDebate, 5000);
    const pollComments = setInterval(loadComments, 4000);
    const pollSpectate = setInterval(pingSpectate, 30000);

    return () => {
      clearInterval(pollDebate);
      clearInterval(pollComments);
      clearInterval(pollSpectate);
    };
  }, [loadDebate, loadComments, pingSpectate]);

  useEffect(() => {
    if (!debate?.expires_at) return;
    const timer = setInterval(() => setTimeLeft(formatTimeLeft(debate.expires_at)), 1000);
    return () => clearInterval(timer);
  }, [debate?.expires_at]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments]);

  const handleVote = async (side) => {
    if (voted) return;
    try {
      await arenaAPI.vote(debateId, side);
      setVoted(side);
      setDebate(prev => ({
        ...prev,
        user_voted: side,
        votes_challenger: side === 'challenger' ? prev.votes_challenger + 1 : prev.votes_challenger,
        votes_opponent: side === 'opponent' ? prev.votes_opponent + 1 : prev.votes_opponent,
        total_votes: prev.total_votes + 1,
      }));
      toast.success('Vote cast!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cannot vote');
    }
  };

  const handleReaction = async (emoji) => {
    try {
      await arenaAPI.addReaction(debateId, emoji);
      const id = reactionIdRef.current++;
      setFloatingReactions(prev => [...prev, { id, emoji }]);
      setTimeout(() => setFloatingReactions(prev => prev.filter(r => r.id !== id)), 2200);
    } catch (err) { console.error('Failed to add reaction', err); }
  };

  const handleComment = async () => {
    if (!newComment.trim()) return;
    try {
      const res = await arenaAPI.addComment(debateId, newComment);
      setComments(prev => [...prev, res.data]);
      setNewComment('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to comment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!debate) return null;

  const totalVotes = debate.votes_challenger + debate.votes_opponent;
  const pctA = totalVotes > 0 ? Math.round((debate.votes_challenger / totalVotes) * 100) : 50;
  const pctB = 100 - pctA;
  const isParticipant = debate.challenger_id === JSON.parse(sessionStorage.getItem('user') || '{}').id ||
                        debate.opponent_id === JSON.parse(sessionStorage.getItem('user') || '{}').id;

  return (
    <div className="min-h-screen bg-gray-950 text-white" data-testid="spectator-view">
      {/* Top Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gray-950/90 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <button onClick={() => navigate('/arena')} className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors" data-testid="back-to-arena">
            <FaArrowLeft size={14} />
            <span className="text-sm font-medium">Arena</span>
          </button>
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-1.5 text-gray-400">
              <FaEye size={12} />
              <span className="text-xs font-medium" data-testid="spectator-count">{spectatorCount} watching</span>
            </div>
            <div className="flex items-center space-x-1.5 text-gray-400">
              <FaClock size={12} />
              <span className="text-xs font-medium" data-testid="time-left">{timeLeft || formatTimeLeft(debate.expires_at)}</span>
            </div>
            <span className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full ${
              debate.status === 'voting' ? 'bg-blue-500/20 text-blue-400' :
              debate.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
              'bg-gray-500/20 text-gray-400'
            }`}>{debate.status}</span>
          </div>
        </div>
      </div>

      <div className="pt-16 max-w-6xl mx-auto px-4">
        {/* Topic */}
        <div className="text-center py-6">
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 bg-white/5 px-3 py-1 rounded-full">{debate.category}</span>
          <h1 className="text-2xl sm:text-3xl font-bold mt-3 leading-tight" data-testid="debate-topic">{debate.topic}</h1>
          <p className="text-sm text-gray-500 mt-2">{totalVotes} vote{totalVotes !== 1 ? 's' : ''}</p>
        </div>

        {/* Vote Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2 px-1">
            <span className={`text-sm font-bold ${voted === 'challenger' || !voted ? 'text-blue-400' : 'text-gray-500'}`}>
              {debate.challenger_name} {pctA}%
            </span>
            <span className={`text-sm font-bold ${voted === 'opponent' || !voted ? 'text-orange-400' : 'text-gray-500'}`}>
              {pctB}% {debate.opponent_name}
            </span>
          </div>
          <div className="h-3 bg-white/5 rounded-full overflow-hidden flex" data-testid="vote-bar">
            <motion.div
              className="bg-gradient-to-r from-blue-500 to-blue-400 rounded-l-full"
              initial={{ width: '50%' }}
              animate={{ width: `${pctA}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            />
            <motion.div
              className="bg-gradient-to-r from-orange-400 to-orange-500 rounded-r-full"
              initial={{ width: '50%' }}
              animate={{ width: `${pctB}%` }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Arguments */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 gap-4 mb-6">
              {/* Challenger */}
              <button
                onClick={() => !isParticipant && handleVote('challenger')}
                disabled={!!voted || isParticipant}
                data-testid="spectator-vote-challenger"
                className={`relative rounded-2xl p-5 transition-all text-left ${
                  voted === 'challenger' ? 'bg-blue-500/10 ring-2 ring-blue-500/40' :
                  voted ? 'bg-white/[0.02] opacity-50' :
                  isParticipant ? 'bg-white/[0.03]' :
                  'bg-white/[0.03] hover:bg-blue-500/10 hover:ring-1 hover:ring-blue-500/20 cursor-pointer'
                }`}
              >
                {voted === 'challenger' && (
                  <div className="absolute top-3 right-3 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <FaCheck className="text-white" size={10} />
                  </div>
                )}
                <div className="flex items-center space-x-3 mb-4">
                  <img src={debate.challenger_avatar || 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg'} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-blue-500/30" />
                  <div>
                    <p className="font-bold text-white text-sm">{debate.challenger_name}</p>
                    <p className="text-xs text-gray-500">@{debate.challenger_username}</p>
                  </div>
                </div>
                {debate.challenger_argument ? (
                  <>
                    <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">{debate.challenger_argument.text}</p>
                    {debate.challenger_argument.media_url && (
                      <div className="mt-3 rounded-xl overflow-hidden">
                        {debate.challenger_argument.media_type?.startsWith('image') ? (
                          <img src={debate.challenger_argument.media_url} alt="" className="w-full max-h-48 object-cover rounded-xl" />
                        ) : debate.challenger_argument.media_type?.startsWith('video') ? (
                          <video src={debate.challenger_argument.media_url} controls className="w-full max-h-48 rounded-xl" />
                        ) : null}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-600 italic">Argument pending...</p>
                )}
                {!voted && !isParticipant && (
                  <p className="text-xs text-blue-400 mt-3 font-medium">Click to vote for this side</p>
                )}
              </button>

              {/* Opponent */}
              <button
                onClick={() => !isParticipant && handleVote('opponent')}
                disabled={!!voted || isParticipant}
                data-testid="spectator-vote-opponent"
                className={`relative rounded-2xl p-5 transition-all text-left ${
                  voted === 'opponent' ? 'bg-orange-500/10 ring-2 ring-orange-500/40' :
                  voted ? 'bg-white/[0.02] opacity-50' :
                  isParticipant ? 'bg-white/[0.03]' :
                  'bg-white/[0.03] hover:bg-orange-500/10 hover:ring-1 hover:ring-orange-500/20 cursor-pointer'
                }`}
              >
                {voted === 'opponent' && (
                  <div className="absolute top-3 right-3 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
                    <FaCheck className="text-white" size={10} />
                  </div>
                )}
                <div className="flex items-center space-x-3 mb-4">
                  <img src={debate.opponent_avatar || 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg'} alt="" className="w-12 h-12 rounded-full object-cover ring-2 ring-orange-500/30" />
                  <div>
                    <p className="font-bold text-white text-sm">{debate.opponent_name}</p>
                    <p className="text-xs text-gray-500">@{debate.opponent_username}</p>
                  </div>
                </div>
                {debate.opponent_argument ? (
                  <>
                    <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">{debate.opponent_argument.text}</p>
                    {debate.opponent_argument.media_url && (
                      <div className="mt-3 rounded-xl overflow-hidden">
                        {debate.opponent_argument.media_type?.startsWith('image') ? (
                          <img src={debate.opponent_argument.media_url} alt="" className="w-full max-h-48 object-cover rounded-xl" />
                        ) : debate.opponent_argument.media_type?.startsWith('video') ? (
                          <video src={debate.opponent_argument.media_url} controls className="w-full max-h-48 rounded-xl" />
                        ) : null}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-gray-600 italic">Argument pending...</p>
                )}
                {!voted && !isParticipant && (
                  <p className="text-xs text-orange-400 mt-3 font-medium">Click to vote for this side</p>
                )}
              </button>
            </div>

            {/* Reactions */}
            <div className="relative mb-6">
              <div className="flex items-center justify-center space-x-2">
                {Object.entries(EMOJI_MAP).map(([key, { icon, label }]) => (
                  <button
                    key={key}
                    onClick={() => handleReaction(key)}
                    data-testid={`reaction-${key}`}
                    className="group relative w-10 h-10 bg-white/5 hover:bg-white/10 rounded-full flex items-center justify-center transition-all hover:scale-110 active:scale-95"
                    title={label}
                  >
                    <span className="text-lg">{icon}</span>
                  </button>
                ))}
              </div>
              {/* Floating reactions */}
              <div className="absolute bottom-12 left-1/2 -translate-x-1/2 w-40 h-32 pointer-events-none">
                <AnimatePresence>
                  {floatingReactions.map(r => (
                    <FloatingReaction key={r.id} emoji={r.emoji} id={r.id} />
                  ))}
                </AnimatePresence>
              </div>
            </div>
          </div>

          {/* Live Chat */}
          <div className="lg:col-span-4">
            <div className="bg-white/[0.03] rounded-2xl border border-white/5 flex flex-col h-[500px]" data-testid="spectator-chat">
              <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-300">Live Chat</h3>
                <span className="text-[11px] text-gray-500">{comments.length} messages</span>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {comments.length === 0 ? (
                  <p className="text-sm text-gray-600 text-center py-8">No comments yet. Be the first!</p>
                ) : (
                  comments.map(c => (
                    <div key={c.id} className="flex space-x-2" data-testid={`comment-${c.id}`}>
                      <img src={c.avatar_url || 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg'} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-xs font-semibold text-gray-400">{c.full_name}</span>
                        <p className="text-sm text-gray-300">{c.text}</p>
                      </div>
                    </div>
                  ))
                )}
                <div ref={commentsEndRef} />
              </div>

              <div className="p-3 border-t border-white/5">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleComment()}
                    placeholder="Say something..."
                    className="flex-1 bg-white/5 rounded-full px-4 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:ring-1 focus:ring-white/10"
                    data-testid="spectator-comment-input"
                  />
                  <button
                    onClick={handleComment}
                    className="bg-white/10 hover:bg-white/15 text-white rounded-full px-3 py-2 transition-all"
                    data-testid="spectator-comment-send"
                  >
                    <FaPaperPlane size={12} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpectatorView;
